/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import styled from "styled-components";
import playIcon from "images/play-icon-hollow.svg";
import pauseIcon from "images/pause-icon-hollow.svg";

const CircleButton = styled.button<{ diameterPx: number; isLoaded?: boolean }>`
  width: ${({ diameterPx }): string => `${diameterPx}px`};
  min-width: ${({ diameterPx }): string => `${diameterPx}px`};
  max-width: ${({ diameterPx }): string => `${diameterPx}px`};
  height: ${({ diameterPx }): string => `${diameterPx}px`};
  min-height: ${({ diameterPx }): string => `${diameterPx}px`};
  max-height: ${({ diameterPx }): string => `${diameterPx}px`};
  padding: 0;
  margin: 0;
  border: 0;
  border-radius: 50%;
  background-color: var(--spectrum-gray-200);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${({ isLoaded = true }): string | null =>
      isLoaded ? "var(--spectrum-gray-300)" : "var(--spectrum-gray-200)"};
    cursor: ${({ isLoaded = true }): string | null =>
      isLoaded ? "pointer" : "default"};
  }
`;

const ButtonIcon = styled.img`
  width: 22px;
  height: 22px;
`;

const PlayPauseButton: React.FC<{
  isPlaying: boolean;
  trackDescription: string;
  buttonDiameterPx: number;
  onClick: () => void;
  isLoaded?: boolean;
}> = ({
  isPlaying,
  trackDescription,
  buttonDiameterPx,
  onClick,
  isLoaded = true,
}) => {
  if (isPlaying) {
    return (
      <CircleButton
        onClick={onClick}
        data-testid="track-preview-pause-button"
        diameterPx={buttonDiameterPx}
        name={I18n.t("track_preview_button.pause_track", {
          trackDescription: trackDescription,
        })}
      >
        <ButtonIcon src={pauseIcon} alt="" />
      </CircleButton>
    );
  } else {
    const playButtonContent = isLoaded ? (
      <ButtonIcon
        src={playIcon}
        alt={I18n.t("track_preview_button.play_track", {
          trackDescription: trackDescription,
        })}
      />
    ) : (
      <sp-progress-circle
        size="s"
        indeterminate
        label={I18n.t("enhance_speech.premium.loading_audio")}
      />
    );

    return (
      <CircleButton
        onClick={(): void => {
          if (isLoaded) onClick();
        }}
        diameterPx={buttonDiameterPx}
        name={I18n.t("track_preview_button.play_track", {
          trackDescription: trackDescription,
        })}
        isLoaded={isLoaded}
      >
        {playButtonContent}
      </CircleButton>
    );
  }
};

export default PlayPauseButton;
